import { useState, useEffect, useCallback, useMemo } from "react";
import { ChevronDown, ChevronRight, Check, Search, Trash2, Loader2 } from "lucide-react";
import type { FeatureFlag, FeatureFlagOverride } from "@yagni/shared";
import { Button } from "../../ui/button.js";
import { Input } from "../../ui/input.js";
import { Switch } from "../../ui/switch.js";
import { Dialog, DialogContent, DialogTitle } from "../../ui/dialog.js";

interface WorkspaceLite {
  id: string;
  name: string;
  slug: string;
  firstUserEmail: string | null;
}

interface FlagWithDetail {
  flag: FeatureFlag;
  overrides: FeatureFlagOverride[];
  loaded: boolean;
  expanded: boolean;
}

interface WorkMissionEmergencyStopResponse {
  active: boolean;
  scope: "global";
}

/** The raw flag list never shows the fleet stop: it has its own row. */
const WORK_MISSION_STORAGE_KEYS = new Set(["work_missions.emergency_stop"]);

const WORK_MISSION_EMERGENCY_STOP_ENDPOINT =
  "/api/admin/feature-flags/work-missions/emergency-stop";

async function responseError(res: Response): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { error?: unknown };
  return typeof body.error === "string" ? body.error : `Request failed (${res.status})`;
}

function readEmergencyStop(body: WorkMissionEmergencyStopResponse): boolean | null {
  return typeof body.active === "boolean" && body.scope === "global" ? body.active : null;
}

export function FeatureFlagsPanel() {
  const [flags, setFlags] = useState<FlagWithDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState<WorkspaceLite[]>([]);
  const [overrideModalKey, setOverrideModalKey] = useState<string | null>(null);
  const [hasEmergencyStop, setHasEmergencyStop] = useState(false);
  const [emergencyStopped, setEmergencyStopped] = useState<boolean | null>(null);
  const [emergencyLoading, setEmergencyLoading] = useState(false);
  const [emergencySaving, setEmergencySaving] = useState(false);
  const [emergencyError, setEmergencyError] = useState<string | null>(null);

  const loadFlags = useCallback(async () => {
    const res = await fetch("/api/admin/feature-flags");
    if (!res.ok) {
      setLoading(false);
      return;
    }
    const list = (await res.json()) as FeatureFlag[];
    const stopSeeded = list.some((flag) => flag.key === "work_missions.emergency_stop");
    setHasEmergencyStop(stopSeeded);
    setFlags((prev) =>
      list.filter((flag) => !WORK_MISSION_STORAGE_KEYS.has(flag.key)).map((flag) => {
        const existing = prev.find((p) => p.flag.key === flag.key);
        return {
          flag,
          overrides: existing?.overrides ?? [],
          loaded: existing?.loaded ?? false,
          expanded: existing?.expanded ?? false,
        };
      }),
    );
    setLoading(false);
    if (stopSeeded) {
      setEmergencyLoading(true);
      try {
        const stopRes = await fetch(WORK_MISSION_EMERGENCY_STOP_ENDPOINT);
        if (stopRes.ok) {
          const active = readEmergencyStop(
            (await stopRes.json()) as WorkMissionEmergencyStopResponse,
          );
          if (active === null) {
            setEmergencyError("The Work Mission fleet stop response was invalid.");
          } else {
            setEmergencyStopped(active);
            setEmergencyError(null);
          }
        } else {
          setEmergencyError(await responseError(stopRes));
        }
      } catch {
        setEmergencyError("Unable to load the Work Mission fleet stop.");
      } finally {
        setEmergencyLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadFlags();
    void (async () => {
      const res = await fetch("/api/admin/workspaces");
      if (!res.ok) return;
      const rows = (await res.json()) as Array<{
        id: string;
        name: string;
        slug: string;
        first_user_email: string | null;
      }>;
      setWorkspaces(
        rows.map((r) => ({
          id: r.id,
          name: r.name,
          slug: r.slug,
          firstUserEmail: r.first_user_email,
        })),
      );
    })();
  }, [loadFlags]);

  async function loadDetail(key: string): Promise<FeatureFlagOverride[]> {
    const res = await fetch(`/api/admin/feature-flags/${encodeURIComponent(key)}`);
    if (!res.ok) return [];
    const body = (await res.json()) as { flag: FeatureFlag; overrides: FeatureFlagOverride[] };
    return body.overrides;
  }

  async function toggleExpanded(key: string) {
    const current = flags.find((f) => f.flag.key === key);
    if (!current) return;
    if (current.expanded) {
      setFlags((prev) => prev.map((f) => (f.flag.key === key ? { ...f, expanded: false } : f)));
      return;
    }
    if (!current.loaded) {
      const overrides = await loadDetail(key);
      setFlags((prev) =>
        prev.map((f) =>
          f.flag.key === key ? { ...f, overrides, loaded: true, expanded: true } : f,
        ),
      );
      return;
    }
    setFlags((prev) => prev.map((f) => (f.flag.key === key ? { ...f, expanded: true } : f)));
  }

  async function toggleGlobal(key: string, nextValue: boolean) {
    const res = await fetch(`/api/admin/feature-flags/${encodeURIComponent(key)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ defaultEnabled: nextValue }),
    });
    if (!res.ok) return;
    const updated = (await res.json()) as FeatureFlag;
    setFlags((prev) =>
      prev.map((f) => (f.flag.key === key ? { ...f, flag: updated } : f)),
    );
  }

  async function updateEmergencyStop(active: boolean) {
    if (emergencySaving || emergencyStopped === null || active === emergencyStopped) return;

    setEmergencySaving(true);
    setEmergencyError(null);
    try {
      const res = await fetch(WORK_MISSION_EMERGENCY_STOP_ENDPOINT, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ active }),
      });
      if (!res.ok) {
        setEmergencyError(await responseError(res));
        return;
      }
      const next = readEmergencyStop((await res.json()) as WorkMissionEmergencyStopResponse);
      if (next === null) {
        setEmergencyError("The Work Mission fleet stop response was invalid.");
        return;
      }
      setEmergencyStopped(next);
    } catch {
      setEmergencyError("Unable to change the Work Mission fleet stop.");
    } finally {
      setEmergencySaving(false);
    }
  }

  async function clearOverride(key: string, workspaceId: string) {
    const res = await fetch(
      `/api/admin/feature-flags/${encodeURIComponent(key)}/overrides/${encodeURIComponent(workspaceId)}`,
      { method: "DELETE" },
    );
    if (!res.ok) return;
    const overrides = await loadDetail(key);
    setFlags((prev) =>
      prev.map((f) =>
        f.flag.key === key
          ? { ...f, overrides, flag: { ...f.flag, overrideCount: overrides.length } }
          : f,
      ),
    );
  }

  async function refreshAfterOverride(key: string) {
    const overrides = await loadDetail(key);
    setFlags((prev) =>
      prev.map((f) =>
        f.flag.key === key
          ? { ...f, overrides, loaded: true, expanded: true, flag: { ...f.flag, overrideCount: overrides.length } }
          : f,
      ),
    );
  }

  const modalFlag = overrideModalKey
    ? flags.find((f) => f.flag.key === overrideModalKey) ?? null
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin" style={{ color: "var(--text-tertiary)" }} />
      </div>
    );
  }

  const controlCount = flags.length + (hasEmergencyStop ? 1 : 0);
  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
        {controlCount} rollout control{controlCount === 1 ? "" : "s"}. Global defaults
        apply to workspaces without an override. The fleet launch pause overrides every
        workspace.
      </div>

      <div
        className="rounded-[var(--radius-card)] overflow-hidden"
        style={{ background: "var(--print-bg)", border: "1px solid var(--print-border)", boxShadow: "var(--shadow-print)" }}
      >
        {hasEmergencyStop && (
          <WorkMissionEmergencyStopRow
            emergencyStopped={emergencyStopped}
            loading={emergencyLoading}
            saving={emergencySaving}
            error={emergencyError}
            onChange={(active) => void updateEmergencyStop(active)}
          />
        )}
        {controlCount === 0 && (
          <div className="px-4 py-6 text-sm" style={{ color: "var(--text-secondary)" }}>
            No flags defined yet.
          </div>
        )}
        {flags.map((item) => (
          <FlagRow
            key={item.flag.key}
            item={item}
            onToggleExpanded={() => void toggleExpanded(item.flag.key)}
            onToggleGlobal={(value) => void toggleGlobal(item.flag.key, value)}
            onClearOverride={(wsId) => void clearOverride(item.flag.key, wsId)}
            onAddOverride={() => setOverrideModalKey(item.flag.key)}
          />
        ))}
      </div>

      {modalFlag && (
        <AddOverrideModal
          isOpen={true}
          flagKey={modalFlag.flag.key}
          workspaces={workspaces}
          existingOverrideWorkspaceIds={modalFlag.overrides.map((o) => o.workspaceId)}
          onClose={() => setOverrideModalKey(null)}
          onSaved={() => {
            void refreshAfterOverride(modalFlag.flag.key);
            setOverrideModalKey(null);
          }}
        />
      )}
    </div>
  );
}

interface WorkMissionEmergencyStopRowProps {
  emergencyStopped: boolean | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  onChange: (active: boolean) => void;
}

function WorkMissionEmergencyStopRow({
  emergencyStopped,
  loading,
  saving,
  error,
  onChange,
}: WorkMissionEmergencyStopRowProps) {
  return (
    <div
      className="flex flex-col gap-3 px-4 py-3"
      style={{ borderBottom: "1px solid var(--border-subtle)" }}
    >
      <div
        className="flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2"
        role="group"
        aria-label="Work Mission fleet launch status"
        aria-live="polite"
        style={{
          background: emergencyStopped
            ? "var(--error-muted)"
            : "var(--bg-base)",
          borderColor: emergencyStopped
            ? "var(--error)"
            : "var(--border-subtle)",
        }}
      >
        <div className="min-w-0 flex-1">
          <div
            className="text-xs font-semibold"
            style={{
              color: emergencyStopped
                ? "var(--error)"
                : "var(--text-primary)",
            }}
          >
            {emergencyStopped === null
              ? loading
                ? "Loading launch status…"
                : "Launch status unavailable"
              : emergencyStopped
                ? "New launches paused"
                : "New launches allowed"}
          </div>
          <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
            {emergencyStopped
              ? "No new planning or execution Runs, model requests, or publication pushes can start in any workspace. Existing recovery or cleanup can still settle safely."
              : "This global stop pauses new launches in every workspace."}
          </div>
        </div>
        <Button
          size="sm"
          variant={emergencyStopped ? "secondary" : "destructive"}
          onClick={() => onChange(!emergencyStopped)}
          loading={saving}
          disabled={emergencyStopped === null || loading || saving}
        >
          {emergencyStopped ? "Resume launches" : "Pause new launches"}
        </Button>
      </div>
      {(loading || saving) && (
        <div className="text-xs" role="status" aria-live="polite" style={{ color: "var(--text-tertiary)" }}>
          {saving ? "Changing Work Mission launch status…" : "Loading Work Mission launch status…"}
        </div>
      )}
      {error && (
        <div className="text-xs" role="alert" style={{ color: "var(--error)" }}>
          {error}
        </div>
      )}
    </div>
  );
}

interface FlagRowProps {
  item: FlagWithDetail;
  onToggleExpanded: () => void;
  onToggleGlobal: (value: boolean) => void;
  onClearOverride: (workspaceId: string) => void;
  onAddOverride: () => void;
}

function FlagRow({
  item,
  onToggleExpanded,
  onToggleGlobal,
  onClearOverride,
  onAddOverride,
}: FlagRowProps) {
  const { flag, overrides, expanded } = item;
  return (
    <div style={{ borderBottom: "1px solid var(--border-subtle)" }}>
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={onToggleExpanded}
          className="flex items-center gap-2 min-w-0 flex-1 text-left"
          style={{ color: "var(--text-primary)" }}
        >
          {expanded ? (
            <ChevronDown size={14} style={{ color: "var(--text-tertiary)" }} />
          ) : (
            <ChevronRight size={14} style={{ color: "var(--text-tertiary)" }} />
          )}
          <div className="min-w-0 flex-1">
            <div
              className="text-sm font-mono truncate"
              style={{ color: "var(--text-primary)" }}
            >
              {flag.key}
            </div>
            {flag.description && (
              <div className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>
                {flag.description}
              </div>
            )}
          </div>
        </button>

        <div
          className="text-xs px-2 py-0.5 rounded-full"
          style={{
            background: "var(--bg-base)",
            color: "var(--text-tertiary)",
            border: "1px solid var(--border-subtle)",
          }}
        >
          {flag.overrideCount} override{flag.overrideCount === 1 ? "" : "s"}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
            Global
          </span>
          <Switch
            checked={flag.defaultEnabled}
            onCheckedChange={(v) => onToggleGlobal(Boolean(v))}
          />
        </div>
      </div>

      {expanded && (
        <div
          className="px-4 pb-3 pt-1 flex flex-col gap-2"
          style={{ background: "var(--bg-base)" }}
        >
          <div className="flex items-center justify-between">
            <span
              className="text-[11px] uppercase tracking-wider font-semibold"
              style={{ color: "var(--text-tertiary)" }}
            >
              Workspace overrides
            </span>
            <Button size="sm" variant="secondary" onClick={onAddOverride}>
              Add override
            </Button>
          </div>
          {overrides.length === 0 ? (
            <div className="text-xs py-2" style={{ color: "var(--text-secondary)" }}>
              No overrides. All workspaces use the global default.
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {overrides.map((o) => (
                <div
                  key={o.workspaceId}
                  className="flex items-center gap-3 px-3 py-2 rounded"
                  style={{
                    background: "rgba(var(--ink-rgb), 0.04)",
                    border: "1px solid var(--border-subtle)",
                  }}
                >
                  <div className="min-w-0 flex-1">
                    <div
                      className="text-sm truncate"
                      style={{ color: "var(--text-primary)" }}
                    >
                      {o.workspaceName}
                    </div>
                    <div
                      className="text-xs truncate"
                      style={{ color: "var(--text-tertiary)" }}
                    >
                      {o.workspaceSlug}
                    </div>
                  </div>
                  <div
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{
                      background: o.enabled ? "var(--accent-muted)" : "var(--bg-base)",
                      color: o.enabled ? "var(--accent)" : "var(--text-tertiary)",
                      border: `1px solid ${o.enabled ? "var(--accent)" : "var(--border-subtle)"}`,
                    }}
                  >
                    {o.enabled ? "Enabled" : "Disabled"}
                  </div>
                  <button
                    type="button"
                    onClick={() => onClearOverride(o.workspaceId)}
                    className="p-1 rounded"
                    style={{ color: "var(--text-tertiary)" }}
                    aria-label="Clear override"
                    title="Clear override (revert to global default)"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface AddOverrideModalProps {
  isOpen: boolean;
  flagKey: string;
  workspaces: WorkspaceLite[];
  existingOverrideWorkspaceIds: string[];
  onClose: () => void;
  onSaved: () => void;
}

function AddOverrideModal({
  isOpen,
  flagKey,
  workspaces,
  existingOverrideWorkspaceIds,
  onClose,
  onSaved,
}: AddOverrideModalProps) {
  const [workspaceId, setWorkspaceId] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setWorkspaceId("");
      setEnabled(true);
      setSearch("");
      setError(null);
    }
  }, [isOpen]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (s === "") return workspaces;
    return workspaces.filter(
      (w) =>
        w.name.toLowerCase().includes(s) ||
        w.slug.toLowerCase().includes(s) ||
        (w.firstUserEmail?.toLowerCase().includes(s) ?? false),
    );
  }, [workspaces, search]);

  async function handleSubmit() {
    if (workspaceId === "") {
      setError("Select a workspace");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/admin/feature-flags/${encodeURIComponent(flagKey)}/overrides/${encodeURIComponent(workspaceId)}`,
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ enabled }),
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error ?? `Request failed (${res.status})`);
        return;
      }
      onSaved();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogTitle>Set override for {flagKey}</DialogTitle>
        <div className="flex flex-col gap-3 mt-3">
          <div>
            <label
              className="text-xs font-medium"
              style={{ color: "var(--text-secondary)" }}
            >
              Workspace
            </label>
            <div className="relative mt-1">
              <Search
                size={14}
                className="absolute left-2 top-2.5"
                style={{ color: "var(--text-tertiary)" }}
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, slug, or email…"
                className="pl-7"
              />
            </div>
            <div
              className="mt-2 max-h-60 overflow-y-auto rounded"
              style={{ border: "1px solid var(--border)" }}
            >
              {filtered.length === 0 && (
                <div
                  className="px-3 py-2 text-xs"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  No matches.
                </div>
              )}
              {filtered.map((w) => {
                const alreadyOverridden = existingOverrideWorkspaceIds.includes(w.id);
                const selected = workspaceId === w.id;
                return (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => setWorkspaceId(w.id)}
                    className="w-full text-left px-3 py-2 flex items-center gap-2"
                    style={{
                      background: selected ? "rgba(var(--ink-rgb), 0.05)" : "transparent",
                      borderBottom: "1px solid var(--border-subtle)",
                    }}
                  >
                    {selected && (
                      <Check size={14} style={{ color: "var(--accent)" }} />
                    )}
                    <div className="min-w-0 flex-1">
                      <div
                        className="text-sm truncate"
                        style={{ color: "var(--text-primary)" }}
                      >
                        {w.name}
                      </div>
                      <div
                        className="text-xs truncate"
                        style={{ color: "var(--text-tertiary)" }}
                      >
                        {w.slug}
                        {w.firstUserEmail ? ` · ${w.firstUserEmail}` : ""}
                      </div>
                    </div>
                    {alreadyOverridden && (
                      <span
                        className="text-[10px] uppercase tracking-wider"
                        style={{ color: "var(--text-tertiary)" }}
                      >
                        Exists
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={enabled} onCheckedChange={(v) => setEnabled(Boolean(v))} />
            <span className="text-sm" style={{ color: "var(--text-primary)" }}>
              {enabled ? "Enabled for this workspace" : "Disabled for this workspace"}
            </span>
          </div>

          {error && (
            <div className="text-xs" style={{ color: "var(--error)" }}>
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 mt-2">
            <Button variant="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={submitting}>
              Save override
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
